import { useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState, AppDispatch } from 'slices'
import { clearBranch } from 'slices/branchSlice'
import { setUserAction, removeUserAction } from './reducer'
import type { AuthUser } from 'Types'

const SUPER_ADMIN_ROLES = ['Super Admin', 'owner']

const ROLE_HIERARCHY: Record<string, number> = {
 'Super Admin': 100,
 owner: 90,
 partner: 80,
 branch_manager: 70,
 accountant: 60,
 inventory_manager: 50,
 cashier: 30,
 waiter: 20,
}

export function useAuth() {
 const user = useSelector((state: RootState) => state.User) as AuthUser | null
 const dispatch = useDispatch<AppDispatch>()

 const isSuperAdmin = Boolean(
 user?.is_super_admin || user?.roles?.some((r) => SUPER_ADMIN_ROLES.includes(r))
 )

 const hasPermission = useCallback(
 (name: string) => {
 if (!user) return false
 if (isSuperAdmin) return true
 return Boolean(user.permissions?.includes(name))
 },
 [user, isSuperAdmin]
 )

 const hasRole = useCallback(
 (name: string | string[]) => {
 if (!user) return false
 if (isSuperAdmin) return true

 const roles = Array.isArray(name) ? name : [name]
 return Boolean(user.roles?.some((r) => roles.includes(r)))
 },
 [user, isSuperAdmin]
 )

 const hasMinRole = useCallback(
 (minRole: string) => {
 if (!user) return false
 if (isSuperAdmin) return true

 const minLevel = ROLE_HIERARCHY[minRole] ?? 0
 return Boolean(
 user.roles?.some((r) => (ROLE_HIERARCHY[r] ?? 0) >= minLevel)
 )
 },
 [user, isSuperAdmin]
 )

 const isOwner = Boolean(user?.roles?.includes('owner'))
 const isPartner = Boolean(user?.roles?.includes('partner'))
 /** Matches server `UserBranchAccess::canUseGlobalView()` (owner/partner/flag/multi-branch rules). */
 const canUseGlobalView = Boolean(user?.can_use_global_view)
 /** Owner, Super Admin, or partner — tenant-wide branch directory + “full” global UX copy. */
 const isTenantWideOperator = Boolean(isSuperAdmin || isPartner)
 const isBranchManager = Boolean(user?.roles?.includes('branch_manager'))
 const isCashier = Boolean(
  user?.role === 'cashier' ||
  user?.roles?.some((r) => {
   const normalized = String(r).trim().toLowerCase()
   return normalized === 'cashier'
  })
 )
 const isAccountant = Boolean(user?.roles?.includes('accountant'))
 const isInventoryManager = Boolean(user?.roles?.includes('inventory_manager'))
 const isWaiter = Boolean(user?.roles?.includes('waiter'))

 const setUser = useCallback(
 (u: AuthUser) => dispatch(setUserAction(u)),
 [dispatch]
 )

 const clearUser = useCallback(() => {
 dispatch(removeUserAction())
 dispatch(clearBranch())
 }, [dispatch])

 return useMemo(
 () => ({
 user,
 isSuperAdmin,
 isOwner,
 isPartner,
 canUseGlobalView,
 isTenantWideOperator,
 isBranchManager,
 isCashier,
 isAccountant,
 isInventoryManager,
 isWaiter,
 hasPermission,
 hasRole,
 hasMinRole,
 setUser,
 clearUser,
 }),
 [
 user, isSuperAdmin, isOwner, isPartner, canUseGlobalView, isTenantWideOperator, isBranchManager,
 isCashier, isAccountant, isInventoryManager, isWaiter,
 hasPermission, hasRole, hasMinRole, setUser, clearUser,
 ]
 )
}
